/**
 * WebSocket hook for real-time task board updates — Phase 2
 *
 * Connects to the backend WS server and invalidates the React Query task cache
 * whenever any task event arrives. Reconnects automatically with exponential backoff.
 *
 * Backend must emit JSON messages in the shape:
 *   { "type": "task:created" | "task:updated" | "task:moved" | "task:commented", "data": {...} }
 *
 * Set VITE_WS_URL in .env to enable (e.g. wss://stage-v2.getinfinitybox.com).
 * When VITE_TASK_BOARD_USE_API=false this hook is a no-op.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const WS_URL = import.meta.env.VITE_WS_URL as string | undefined;
const USE_API = import.meta.env.VITE_TASK_BOARD_USE_API === 'true';

const TASK_EVENTS = new Set([
  'task:created',
  'task:updated',
  'task:moved',
  'task:commented',
]);

export function useTaskSocket(userId: number) {
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attempts = useRef(0);
  const destroyed = useRef(false);

  useEffect(() => {
    if (!USE_API || !WS_URL) return;

    destroyed.current = false;

    function connect() {
      if (destroyed.current) return;

      const url = `${WS_URL}/tasks?userId=${userId}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attempts.current = 0;
        console.log('[TaskSocket] Connected');
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string) as { type: string };
          if (TASK_EVENTS.has(msg.type)) {
            queryClient.invalidateQueries({ queryKey: ['tasks', userId] });
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (destroyed.current) return;
        // Exponential backoff: 1s, 2s, 4s, 8s … max 30s
        const delay = Math.min(1000 * 2 ** attempts.current, 30_000);
        attempts.current += 1;
        console.log(`[TaskSocket] Disconnected. Reconnecting in ${delay}ms (attempt ${attempts.current})`);
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close(); // triggers onclose → reconnect
      };
    }

    connect();

    return () => {
      destroyed.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [userId, queryClient]);
}
